/**
 * MobileStickyCta — CTA collant en bas d'écran sur mobile/tablette.
 *
 * Objectif conversion : maximiser l'entrée en funnel depuis les pages de contenu
 * (audiences, blog, secteurs) où le CTA hero est sorti du viewport au scroll.
 *
 * - Routé par audience effective (lock > URL > store) via AUDIENCE_NAV.
 * - Apparaît après un seuil de scroll, masqué tant que l'utilisateur est en haut.
 * - Expérience A/B `exp_mobile_sticky_cta` : actif par défaut (fallback 'sticky'),
 *   un groupe témoin peut le désactiver via PostHog (variante 'control').
 * - Caché sur desktop (lg+), où le header porte déjà un CTA contextuel.
 *
 * À monter uniquement sur le layout 'classic' (pas dans les funnels).
 */

import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useEffectiveAudience } from '../../hooks/useEffectiveAudience';
import { useInProgressJourney } from '../../hooks/useInProgressJourney';
import { isResumeBannerDismissed, RESUME_DISMISS_EVENT } from './ResumeJourneyBanner';
import { AUDIENCE_NAV } from '../../config/audience-nav';
import { ROUTES } from '@shared/constants/routes';
import { useExperiment, captureEvent, trackExperimentConversion } from '../../lib/analytics';
import { EXPERIMENTS } from '../../config/experiments';
import { Button } from '../ui/button';

const SHOW_AFTER_PX = 560;

export function MobileStickyCta() {
  const { effective } = useEffectiveAudience();
  const journey = useInProgressJourney();
  const { variant } = useExperiment<'sticky' | 'control'>(
    EXPERIMENTS.mobileStickyCta.key,
    'sticky',
  );
  const [visible, setVisible] = useState(false);
  const [resumeDismissed, setResumeDismissed] = useState(false);

  useEffect(() => {
    setResumeDismissed(isResumeBannerDismissed());
    const onDismiss = () => setResumeDismissed(true);
    window.addEventListener(RESUME_DISMISS_EVENT, onDismiss);
    return () => window.removeEventListener(RESUME_DISMISS_EVENT, onDismiss);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      // Masquer près du bas de page pour ne pas chevaucher le footer/CTA final.
      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 320;
      setVisible(y > SHOW_AFTER_PX && !nearBottom);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Masquer uniquement si le bandeau de reprise est visible (parcours + non dismiss).
  // Si l'utilisateur ferme le bandeau, le sticky CTA reprend le relais.
  const enabled = variant !== 'control' && !(journey && !resumeDismissed);

  // Padding bas du #main uniquement quand le sticky est réellement affiché.
  useEffect(() => {
    const main = document.getElementById('main');
    if (!main) return;
    if (visible && enabled) {
      main.style.paddingBottom = 'calc(4.5rem + env(safe-area-inset-bottom))';
    } else {
      main.style.paddingBottom = '';
    }
    return () => {
      main.style.paddingBottom = '';
    };
  }, [visible, enabled]);

  // Groupe témoin A/B → pas de sticky
  if (!enabled) return null;

  const cta = effective
    ? AUDIENCE_NAV[effective.id].primaryCta
    : { label: 'Voir nos outils', href: ROUTES.outils };

  const handleClick = () => {
    captureEvent('cta_sticky_click', {
      audience: effective?.id ?? 'none',
      href: cta.href,
      label: cta.label,
    });
    trackExperimentConversion(EXPERIMENTS.mobileStickyCta.key, variant, 'funnel_start', {
      audience: effective?.id ?? 'none',
    });
  };

  // Ne pas monter le lien hors écran : évite le focus clavier sur un CTA invisible.
  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 lg:hidden">
      <div className="mx-auto max-w-md rounded-2xl border border-stone-200 bg-white/95 p-2 shadow-[0_8px_32px_rgba(10,10,10,.16)] backdrop-blur-md">
        <Button asChild variant="brand" size="xl" className="h-12 w-full rounded-xl text-[15px]">
          <a href={cta.href} onClick={handleClick}>
            {cta.label}
            <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
          </a>
        </Button>
      </div>
    </div>
  );
}
